import InfiniteScroll from 'react-infinite-scroller'

import {
  SpaceJoinRequestStatus,
  SpaceJoinRequest,
  Member,
} from '@tribeplatform/gql-client/types'
import { MemberAvatar } from '@tribeplatform/react-components/common/components/member'
import { T, useI18n } from '@tribeplatform/react-components/i18n'
import { useRouter } from '@tribeplatform/react-sdk'
import {
  useApproveSpaceMembershipRequest,
  useDeclineSpaceMembershipRequest,
  useSpace,
  useSpaceJoinRequests,
} from '@tribeplatform/react-sdk/hooks'
import { simplifyPaginatedResult } from '@tribeplatform/react-sdk/utils'
import { Alert } from '@tribeplatform/react-ui-kit/Alert'
import { IconButton, Button } from '@tribeplatform/react-ui-kit/Button'
import { Card } from '@tribeplatform/react-ui-kit/Card'
import { Dropdown } from '@tribeplatform/react-ui-kit/Dropdown'
import { SvgIcon } from '@tribeplatform/react-ui-kit/Icon'
import { Link } from '@tribeplatform/react-ui-kit/Link'

import { MemberActionItems } from '../Member/MemberActionItems.js'
import { EmptyState } from '../Moderation/EmptyState.js'

const PendingRequestLoading = ({ count = 5 }) => {
  return (
    <>
      {[...Array(count)].map((_e, i) => (
        <div
          // eslint-disable-next-line react/no-array-index-key
          key={i}
          className="bg-surface px-4 py-6 shadow sm:p-6 sm:rounded-lg"
        >
          <div className="animate-pulse flex space-s-4">
            <div className="rounded-avatar bg-skeleton h-12 w-12" />
            <div className="flex-1 space-y-4 py-1">
              <div className="h-4 bg-skeleton rounded-full w-3/4" />
              <div className="space-y-2">
                <div className="h-4 bg-skeleton rounded-full" />
                <div className="h-4 bg-skeleton rounded-full w-5/6" />
                <div className="h-4 bg-skeleton rounded-full" />
              </div>
            </div>
          </div>
        </div>
      ))}
    </>
  )
}

const SpaceMembershipNote = ({
  spaceId,
  member,
}: {
  spaceId: string
  member: Member
}) => {
  const { data: space } = useSpace({
    variables: {
      id: spaceId,
    },
    fields: {
      image: 'all',
    },
  })
  const { Link: RouterLink } = useRouter()
  return (
    <Alert status="neutral" className="font-medium leading-8">
      <T
        defaultMessage="{name} wants to join {spaceName}"
        id="SpaceMembershipRequest.Alert"
        values={{
          name: (
            <span>
              <Link
                href={member?.relativeUrl}
                variant="neutral"
                as={RouterLink}
              >
                {member?.name}
              </Link>
            </span>
          ),

          spaceName: (
            <Link href={space?.relativeUrl} variant="neutral" as={RouterLink}>
              {space?.name}
            </Link>
          ),
        }}
      />
    </Alert>
  )
}

export const SpaceMembershipRequests = ({ spaceId }) => {
  const { $t } = useI18n()

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isInitialLoading: isLoading,
  } = useSpaceJoinRequests({
    variables: {
      limit: 5,
      spaceId,
      status: SpaceJoinRequestStatus.PENDING,
    },
    fields: {
      pageInfo: 'basic',
      nodes: {
        member: {
          profilePicture: 'all',
          authMemberProps: {
            permissions: 'all',
          },
        },
      },
    },
  })

  const requests = simplifyPaginatedResult<SpaceJoinRequest>(data)?.nodes

  const { mutate: approve, isLoading: approveLoading } =
    useApproveSpaceMembershipRequest()
  const { mutate: decline, isLoading: declineLoading } =
    useDeclineSpaceMembershipRequest()
  const onApprove = (request: SpaceJoinRequest) => {
    approve({
      spaceId: request.spaceId,
      spaceMembershipRequestId: request.id,
    })
  }
  const onDecline = (request: SpaceJoinRequest) => {
    decline({
      spaceId: request.spaceId,
      spaceMembershipRequestId: request.id,
    })
  }

  if (!isLoading && !requests?.length) {
    return <EmptyState />
  }

  return (
    <InfiniteScroll
      pageStart={0}
      loadMore={fetchNextPage}
      hasMore={hasNextPage || isLoading}
      threshold={800}
      className="flex flex-col space-y-5"
      loader={<PendingRequestLoading />}
    >
      {requests?.map(request => (
        <Card key={request.id}>
          <Card.Content className="flex flex-col gap-5">
            <SpaceMembershipNote
              spaceId={request?.spaceId}
              member={request?.member}
            />
            <Link
              href={request.member?.relativeUrl}
              rel="noopener noreferrer"
              target="_blank"
            >
              <div className="flex items-center">
                <div className="flex-shrink-0 h-10 w-10">
                  <MemberAvatar size="2.5x" member={request.member} />
                </div>
                <div className="ms-4">
                  <div
                    className="text-sm font-medium text-content"
                    translate="no"
                  >
                    {request.member?.name}
                  </div>
                  <div className="text-sm text-content-subdued">
                    {request.member?.tagline}
                  </div>
                </div>
                <div className="flex-1 flex justify-end">
                  <Dropdown placement="bottom-end">
                    <Dropdown.ButtonMinimal
                      aria-label={$t({
                        defaultMessage: 'Options',
                        id: 'Generics.Options',
                      })}
                    >
                      <IconButton
                        as="span"
                        variant="tertiaryNeutral"
                        rounded
                        icon={<SvgIcon name="dots-horizontal" />}
                      />
                    </Dropdown.ButtonMinimal>
                    <MemberActionItems
                      member={request?.member}
                      context="moderation"
                    />
                  </Dropdown>
                </div>
              </div>
            </Link>
            <div className="flex gap-3">
              <Button
                loading={approveLoading}
                disabled={approveLoading || declineLoading}
                variant="primary"
                onClick={() => {
                  onApprove(request)
                }}
                className="flex-1"
              >
                <T defaultMessage="Approve" id="Generics.Approve" />
              </Button>
              <Button
                loading={declineLoading}
                disabled={approveLoading || declineLoading}
                variant="secondaryNeutral"
                onClick={() => {
                  onDecline(request)
                }}
                className="flex-1"
              >
                <T defaultMessage="Decline" id="Generics.Decline" />
              </Button>
            </div>
          </Card.Content>
        </Card>
      ))}
    </InfiniteScroll>
  )
}
